/** @jsxImportSource @emotion/react */

import {
  CalciteTextArea,
  CalciteTooltip,
} from "@esri/calcite-components-react";
import { AxiosError } from "axios";
import { Button } from "components/Button";
import { Card } from "components/Card";
import DraggableModal from "components/Moveables/DraggableModal";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import { UserContext } from "context/UserContext";
import { loadRecord } from "pages/ALI/ALIRead";
import { useContext, useEffect, useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import RecordService from "services/RecordService";
import tw from "twin.macro";
import {
  AliChangeResponse,
  AliErrorChange,
  Discrepancy,
  DiscrepData,
  LVFResp,
  MsagChangeResponse,
} from "../../services/DiscrepancyType";
import CallGenerated from "./CallGenerated";
import { ChangeRequestTable } from "./ChangeRequestTable";
import Lvf from "./Lvf";
import MsagChangeTable from "./MsagChangeTable";
import { DetailTable, NotesTable } from "./RecordTable";
import SystemGenerated from "./SystemGenerated";

const recordService = new RecordService("discrep");
const aliService = new RecordService("ali");
const commentService = new RecordService("notes");

export const loadDiscrepancy = async ({ params }) => {
  const res = await recordService.getRecords(
    {
      criterias: [{ field: "id", type: "=", value: params.id }],
    },
    "searchByCriteria",
    true,
  );

  if (res.data.message.Size === 0) {
    return {};
  } else {
    const discrepancy = res.data.message.List[0];
    return discrepancy;
  }
};

export interface Note {
  idx: number;
  discrep_id: number;
  submitted?: string;
  submitted_by?: string;
  note?: string;
}

export default function ViewDiscrepancy() {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const navigate = useNavigate();

  const data: DiscrepData | any = useLoaderData() as DiscrepData | any;
  const [discrepancy, setDiscrepancy] = useState<Discrepancy | null>(null);
  const [aliChangeData, setAliChangeData] = useState<AliChangeResponse | null>(
    null,
  );
  const [lvfChangeData, setLvfChangeData] = useState<LVFResp | null>(null);
  const [msagChangeData, setMsagChangeData] =
    useState<MsagChangeResponse | null>(null);
  const [aliErrorData, setAliErrorData] = useState<AliErrorChange | null>(null);
  const [discrepType, setDiscrepType] = useState<string>("");
  const [showEditAliRecord, setShowEditAliRecord] = useState<boolean>(false);

  const [newNote, setNewNote] = useState<string>("");
  const [isQuestion, setIsQuestion] = useState<number>(0);
  const [showNotesModal, setShowNotesModal] = useState<boolean | undefined>(
    undefined,
  );
  const [notes, setNotes] = useState<Note[]>([]);
  const [error, setError] = useState<AxiosError | null>(null);

  const getData = async (data: Discrepancy) => {
    await recordService
      .getDiscrepancyData(data)
      .then((res: DiscrepData) => {
        if (res !== undefined) {
          switch (res.table_name) {
            case "ali_change_req":
              if (res.ali_change_response !== undefined) {
                setAliChangeData(res.ali_change_response);
                setDiscrepType("aliChange");
              }
              break;
            case "lvf":
              if (res.lvf_response !== undefined) {
                setLvfChangeData(res.lvf_response);
                setDiscrepType("discrepancyLvf");
              }
              break;
            case "msag_change_req":
              if (res.msag_change_response !== undefined) {
                setMsagChangeData(res.msag_change_response);
                setDiscrepType("msagChangeReq");
              }
              break;
            case "DISCREP_ALIERROR":
              if (res.ali_error_response !== undefined) {
                setAliErrorData(res.ali_error_response.error);
                setDiscrepType("discrepancyAliError");
              }
              break;
            case "DISCREP_ALI_ERROR_SYSTEMGENERATED":
              if (res.ali_error_response !== undefined) {
                setAliErrorData(res.ali_error_response.error);
                setDiscrepType("discrepancyAliErrorSystemGenerated");
              }
              break;
            default:
              break;
          }
        }
      })
      .catch((error) => {
        setError(error);
      });

    if (discrepancy?.ANI)
      await loadRecord({ params: { cpn: discrepancy?.ANI } })
        .then(() => {
          setShowEditAliRecord(true);
        })
        .catch(() => {
          setShowEditAliRecord(false);
        });
  };

  const getNotes = async (data: Discrepancy) => {
    const res = await commentService.getNotes(data);
    setNotes(res.data.message);
  };

  const addNote = async () => {
    try {
      const discrepData =
        discrepType === "aliChange"
          ? aliChangeData
          : discrepType === "discrepancyLvf"
            ? lvfChangeData
            : discrepType === "msagChangeReq"
              ? msagChangeData
              : discrepType === "discrepancyAliError"
                ? aliErrorData
                : discrepType === "discrepancyAliErrorSystemGenerated"
                  ? aliErrorData
                  : null;

      if (isQuestion === 1) {
        const question = await recordService.updateDiscrepancy(
          discrepType,
          {
            id: data.id,
            // table_name: data.table_name,
            Criteria: [
              {
                field: "status",
                value: data?.status, // keep current status
              },
            ],
            //   {
            //     field: "question",
            //     value: "1",
            //   },
            // ],
          },
          undefined,
          1,
        );
        console.log(question);
        setIsQuestion(1);
      }
      const createNewNote = await commentService.addNotes(data, newNote);
      if (createNewNote !== undefined) {
        if (Array.isArray(notes)) setNotes([...notes, createNewNote]);
        else setNotes([createNewNote]);

        addNotification({
          title: isQuestion === 1 ? "Question Added" : "Note Added",
          message:
            isQuestion === 1
              ? "Question has been added successfully"
              : "Note has been added successfully",
          type: NOTIFICATION_TYPE.SUCCESS,
        });
      }
    } catch (error) {
      addNotification({
        title: "Error",
        message: `Unable to add ${
          isQuestion === 1 ? "question" : "note"
        }: ${error}`,
        type: NOTIFICATION_TYPE.ERROR,
      });
    }
    setShowNotesModal(undefined);
  };

  useEffect(() => {
    if (data) {
      getData(data);
      setDiscrepancy(data);
      getNotes(data);
    }
  }, [isQuestion]);

  const handleQuestion = () => {
    setShowNotesModal(true);
    setIsQuestion(1);
  };

  return (
    <>
      <div
        css={[tw`grid grid-cols-2 gap-3 place-items-stretch`]}
        id="discrepancy-grid"
      >
        {error && (
          <div
            css={[
              tw`col-span-2 min-h-max h-full place-self-center m-auto flex flex-col items-center gap-y-4`,
            ]}
          >
            <span>{error.message}</span>
            <Button onClick={() => navigate("..")}>Go Back</Button>
          </div>
        )}
        {lvfChangeData && discrepType === "discrepancyLvf" && (
          <Lvf data={lvfChangeData} discrep={data} />
        )}
        {aliErrorData && discrepType === "discrepancyAliError" && (
          <CallGenerated error={aliErrorData} discrep={data} />
        )}
        {aliErrorData &&
          discrepType === "discrepancyAliErrorSystemGenerated" && (
            <SystemGenerated error={aliErrorData} />
          )}
        {msagChangeData && discrepType === "msagChangeReq" && (
          <MsagChangeTable
            msag={msagChangeData.msag}
            msag_change={msagChangeData.msag_change}
          />
        )}
        {aliChangeData && discrepType === "aliChange" && (
          <ChangeRequestTable
            ali={aliChangeData.ali}
            ali_change={aliChangeData.ali_change}
          />
        )}
        <div css={[tw`col-span-1`]}>
          <Card
            aria-label="Notes"
            css={[tw`drop-shadow-sm border border-gray-100`]}
          >
            <div css={[tw`flex justify-between`]}>
              <h3>Notes</h3>
              <Button
                variant="outline"
                id="new-note-button"
                iconStart="speech-bubble-plus"
                onClick={() => {
                  setIsQuestion(0);
                  setShowNotesModal(true);
                }}
                disabled={showNotesModal === true}
              >
                New Note
              </Button>
            </div>

            <div css={[tw`border border-gray-300 rounded-md overflow-hidden`]}>
              <NotesTable setData={setNotes} data={notes} />
            </div>
          </Card>
        </div>
        <div css={[tw`col-span-1  `]}>
          <Card
            aria-label="Details"
            css={[tw`drop-shadow-sm border border-gray-100`]}
          >
            <div css={[tw`flex justify-between`]}>
              <h3>Details</h3>
              <div css={[tw`flex gap-2`]}>
                <Button
                  variant="outline"
                  iconStart="geonet-question"
                  onClick={handleQuestion}
                  disabled={showNotesModal === true}
                >
                  New Question
                </Button>
                {showEditAliRecord &&
                  (user?.functions?.includes("foresite:ali:write") ||
                    user?.functions?.includes(
                      "foresite:ali:requestChange",
                    )) && (
                    <>
                      <CalciteTooltip
                        referenceElement={"editAliRecord"}
                        placement="left"
                      >
                        Edit ALI Record
                      </CalciteTooltip>
                      <Button
                        id="editAliRecord"
                        variant="solid"
                        iconStart="derived-value"
                        onClick={() => {
                          navigate(`/ali-records/edit/${discrepancy?.ANI}`);
                        }}
                      ></Button>
                    </>
                  )}
              </div>
            </div>
            <div
              css={[
                tw`border-r border-t border-l border-gray-300 rounded-md overflow-hidden`,
              ]}
            >
              {(() => {
                const resolvedDiscrepData =
                  discrepType === "aliChange"
                    ? aliChangeData?.ali_change
                    : discrepType === "discrepancyLvf"
                      ? lvfChangeData?.lvf
                      : discrepType === "msagChangeReq"
                        ? msagChangeData
                        : discrepType === "discrepancyAliError" ||
                            discrepType === "discrepancyAliErrorSystemGenerated"
                          ? aliErrorData
                          : null;

                return resolvedDiscrepData ? (
                  <DetailTable
                    isQuestion={isQuestion}
                    setIsQuestion={setIsQuestion}
                    setShowNotesModal={setShowNotesModal}
                    data={data}
                    discrepData={resolvedDiscrepData}
                    discrepType={discrepType}
                  />
                ) : (
                  <div css={[tw`p-3 text-gray-600 text-sm`]}>
                    No details available for this discrepancy.
                  </div>
                );
              })()}
            </div>
          </Card>
        </div>
      </div>
      <DraggableModal
        children={
          <CalciteTextArea
            form="noteForm"
            required={true}
            rows={3}
            wrap="soft"
            maxLength={2000}
            autoFocus={true}
            style={{ height: "100px" }}
            resize="none"
            scale="m"
            value={newNote}
            onCalciteTextAreaInput={(e) => {
              e.stopPropagation();
              setNewNote(e.target.value);
            }}
          ></CalciteTextArea>
        }
        title={isQuestion ? "Add Question" : "Add Note"}
        id="newNoteModal"
        isOpen={showNotesModal}
        setIsOpen={setShowNotesModal}
        onSave={addNote}
        onCancel={
          isQuestion
            ? () => {
                if (!discrepancy?.question) setIsQuestion(0);
              }
            : undefined
        }
        initialPosition={{ x: 120, y: Math.min(window.innerHeight * 0.8, 700) }}
        validation={() => {
          return newNote.length > 0 && newNote.length <= 2000;
        }}
      />
    </>
  );
}
